import type { SoundId } from '../specs/types';
import type { CharacterId } from '../questions/types';

/**
 * Instrument descriptions. The engine reasons about instruments through these
 * profiles rather than special-casing by name, so adding an instrument is data
 * plus a fingering table, not new branches through the question generators.
 */

export type InstrumentId = 'ukulele' | 'guitar' | 'ocarina' | 'piano' | 'voice';

export type InstrumentFamily = 'fretted' | 'wind' | 'keyboard' | 'voice';

export interface Tuning {
  id: string;
  name: string;
  /** Sounding MIDI of each string, ordered lowest-pitched string first. */
  midis: number[];
  /** True when a string is tuned higher than the one below it, as on a ukulele. */
  reentrant?: boolean;
  frets: number;
}

export type HoleId = string;

export type HoleState = 'closed' | 'open' | 'half';

export interface HoleDef {
  id: HoleId;
  label: string;
  side: 'top' | 'bottom';
  /** Position in the widget's 0-100 coordinate box. */
  x: number;
  y: number;
  r: number;
  hand: 'L' | 'R';
  finger: 'thumb' | 'index' | 'middle' | 'ring' | 'pinky' | 'sub';
}

export interface Fingering {
  holes: Record<HoleId, HoleState>;
  /**
   * False until confirmed on a real instrument. Sub-hole and cross-fingering
   * assignments differ between makers, so the app must not present an unverified
   * chart as fact.
   */
  verified: boolean;
  alt?: boolean;
  note?: string;
}

export interface OcarinaVariant {
  id: string;
  name: string;
  holes: HoleDef[];
  /** Keyed by sounding note name, such as "C5" or "F#5". */
  fingerings: Record<string, Fingering>;
  range: { low: number; high: number };
  /** Semitones this variant sounds relative to an alto C. */
  transposeFromAC: number;
}

/** Microphone analysis parameters, tuned per instrument in Phase 4. */
export interface DetectionParams {
  windowSize: 2048 | 4096;
  hopSize: 256 | 512;
  clarityThreshold: number;
  minFreq: number;
  maxFreq: number;
  noteOnFrames: number;
  noteOffFrames: number;
  centsTolerance: number;
  chordMode: 'voicing' | 'chroma' | 'none';
}

export interface InstrumentProfile {
  id: InstrumentId;
  name: string;
  family: InstrumentFamily;
  character: CharacterId;
  sound: SoundId;
  clef: 'treble' | 'bass' | 'treble8vb' | 'grand';
  /** Guitar sounds an octave below where it is written: -1. */
  writtenOctaveOffset: number;
  polyphonic: boolean;
  /** Absolute sounding range. */
  range: { low: number; high: number };
  /** Where generated material should sit by default. */
  comfortable: { low: number; high: number };
  tunings?: Tuning[];
  defaultTuningId?: string;
  variants?: OcarinaVariant[];
  defaultVariantId?: string;
  notationModes: ('staff' | 'tab' | 'fingering' | 'letters' | 'chordDiagram')[];
  widgets: ('fretboard' | 'chordDiagram' | 'ocarina' | 'keyboard')[];
  detection: DetectionParams;
  /** Training wheels: which hint to show, and the mastery at which it fades. */
  hints: { default: 'tab' | 'fingering' | 'none'; fadeAtMastery: number };
}

/** A fretted chord shape. Frets are ordered like the tuning, lowest string first. */
export interface ChordShape {
  id: string;
  /** Chord symbol this shape produces, such as "G7". */
  symbol: string;
  instrument: 'ukulele' | 'guitar';
  /** null mutes the string; 0 is open. */
  frets: (number | null)[];
  /** Fretting-hand finger per string, or null for open/muted. */
  fingers?: (number | null)[];
  barre?: { fret: number; from: number; to: number };
  baseFret?: number;
  /** 1 easiest. Drives which shapes a difficulty band may use. */
  tier: number;
  /** Movable shapes can be transposed up the neck. */
  movable?: boolean;
  tags?: ('open' | 'barre' | 'jazz' | 'movable' | 'power')[];
}

export function isFretted(profile: InstrumentProfile): boolean {
  return profile.family === 'fretted';
}

export function defaultTuning(profile: InstrumentProfile): Tuning | undefined {
  if (profile.tunings === undefined) return undefined;
  return profile.tunings.find((t) => t.id === profile.defaultTuningId) ?? profile.tunings[0];
}

export function defaultVariant(profile: InstrumentProfile): OcarinaVariant | undefined {
  if (profile.variants === undefined) return undefined;
  return profile.variants.find((v) => v.id === profile.defaultVariantId) ?? profile.variants[0];
}
